import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import type { ContractFormValues } from '../contract-form';
import type { ContractServiceConfigOption } from '@/api/contracts';

/**
 * Derives a product line's register price (the amount billed UPFRONT at
 * registration) from its config options and writes it into
 * `products.{index}.service_price_register` (the contract Total is Σ register).
 *
 * Only PREPAID commitments count toward the upfront register total. This mirrors
 * the billing engine, which is the source of truth: for the register/renew
 * scope it SKIPS non-prepaid configs — `datacentral-api-v4`
 * `pkg/billing/inv.calc.statement.go:88` (`if !config.CommitmentPrepaid.Bool { continue }`,
 * "postpaid sẽ được tính trong statement"). Postpaid commitments are reconciled
 * monthly via statement (đối soát), NOT charged upfront — so unchecking Prepaid
 * removes that config from this total. (The legacy client summed all commitments
 * regardless of prepaid, which overstated the upfront amount — not replicated.)
 *
 * Usage-tier overage (`prices[]`) is never included here — it too is statement-billed.
 * `enabled=false` opts a template out entirely; `total` overrides the derivation.
 */
export function useServicePriceTotal(index: number, opts?: { enabled?: boolean; total?: number }) {
  const { control, setValue } = useFormContext<ContractFormValues>();
  const optsPath = `products.${index}.service_configoptions` as const;
  const options = (useWatch({ control, name: optsPath }) ?? []) as ContractServiceConfigOption[];

  const enabled = opts?.enabled ?? true;
  const committed = (options ?? []).reduce(
    (sum, o) =>
      o.commitment_prepaid === true
        ? sum + Number(o.commitment_value ?? 0) * Number(o.commitment_price_per_unit ?? 0)
        : sum,
    0,
  );
  const total = opts?.total ?? committed;

  useEffect(() => {
    if (!enabled) return;
    setValue(`products.${index}.service_price_register` as never, total as never, { shouldDirty: true });
  }, [enabled, index, total, setValue]);
}
